import type { StateCreator } from 'zustand';
import type { AIThread, AIMessage, AIKnowledgeEntry } from '../types/ai';
import * as aiChatService from '../services/aiChat';

export interface AISlice {
  aiThreads:        AIThread[];
  aiActiveThreadId: string | null;
  aiMessages:       Record<string, AIMessage[]>;
  aiKnowledge:      AIKnowledgeEntry[];
  aiIsLoading:      boolean;
  aiIsMemorySaving: boolean;
  aiMemorySaved:    boolean;
  aiIsExpertMode:   boolean;
  aiIncludeSensor:  boolean;
  aiError:          string | null;

  aiLoadThreads:   () => Promise<void>;
  aiSelectThread:  (threadId: string) => Promise<void>;
  aiNewThread:     () => void;
  aiArchiveThread: (threadId: string) => Promise<void>;
  aiSendMessage:   (message: string, imageData?: { base64: string; mediaType: string }) => Promise<void>;
  aiSetExpertMode: (v: boolean) => void;
  aiSetIncludeSensor: (v: boolean) => void;
  aiLoadKnowledge: () => Promise<void>;
}

export const createAISlice: StateCreator<AISlice, [], [], AISlice> = (set, get) => ({
  aiThreads:        [],
  aiActiveThreadId: null,
  aiMessages:       {},
  aiKnowledge:      [],
  aiIsLoading:      false,
  aiIsMemorySaving: false,
  aiMemorySaved:    false,
  aiIsExpertMode:   false,
  aiIncludeSensor:  false,
  aiError:          null,

  aiLoadThreads: async () => {
    const threads = await aiChatService.fetchThreads();
    set({ aiThreads: threads });
  },

  aiSelectThread: async (threadId) => {
    set({ aiActiveThreadId: threadId, aiError: null });
    if (get().aiMessages[threadId]?.length) return;
    const messages = await aiChatService.fetchMessages(threadId);
    set(s => ({ aiMessages: { ...s.aiMessages, [threadId]: messages } }));
  },

  aiNewThread: () => set({ aiActiveThreadId: null, aiError: null }),

  aiArchiveThread: async (threadId) => {
    await aiChatService.archiveThread(threadId);
    set(s => ({
      aiThreads:        s.aiThreads.filter(t => t.id !== threadId),
      aiActiveThreadId: s.aiActiveThreadId === threadId ? null : s.aiActiveThreadId,
    }));
  },

  aiSendMessage: async (message, imageData) => {
    const { aiActiveThreadId, aiIsExpertMode, aiIncludeSensor } = get();
    set({ aiIsLoading: true, aiIsMemorySaving: false, aiMemorySaved: false, aiError: null });

    // Optimistic user message
    const userMsgId = `user-${Date.now()}`;
    const userMsg: AIMessage = {
      id:         userMsgId,
      thread_id:  aiActiveThreadId ?? 'new',
      role:       'user',
      content:    imageData ? `[Fotoğraf eklendi] ${message || 'Görseli analiz et.'}` : message,
      created_at: new Date().toISOString(),
    };

    // Streaming assistant message placeholder
    const assistantMsgId = `stream-${Date.now()}`;
    const assistantMsg: AIMessage = {
      id:         assistantMsgId,
      thread_id:  aiActiveThreadId ?? 'new',
      role:       'assistant',
      content:    '',
      created_at: new Date().toISOString(),
    };

    const threadKey = aiActiveThreadId ?? 'new';
    set(s => ({
      aiMessages: {
        ...s.aiMessages,
        [threadKey]: [...(s.aiMessages[threadKey] ?? []), userMsg, assistantMsg],
      },
    }));

    try {
      await aiChatService.sendMessage(
        {
          message,
          thread_id:             aiActiveThreadId ?? undefined,
          is_expert_mode:        aiIsExpertMode,
          include_sensor_context: aiIncludeSensor,
          ...(imageData ? { image_base64: imageData.base64, image_media_type: imageData.mediaType } : {}),
        },
        {
          onDelta: (text) => {
            set(s => {
              const key = get().aiActiveThreadId ?? threadKey;
              const msgs = s.aiMessages[key] ?? [];
              return {
                aiMessages: {
                  ...s.aiMessages,
                  [key]: msgs.map(m =>
                    m.id === assistantMsgId ? { ...m, content: m.content + text } : m,
                  ),
                },
              };
            });
          },

          onMemory: () => set({ aiIsMemorySaving: true }),

          onDone: (tid, memorySaved) => {
            set(s => {
              const threadExists = s.aiThreads.some(t => t.id === tid);
              const newThreads = threadExists
                ? s.aiThreads
                : [{
                    id: tid, title: message.slice(0, 80), context: 'general' as const,
                    is_archived: false, created_at: new Date().toISOString(), updated_at: new Date().toISOString(),
                  }, ...s.aiThreads];

              const prevMsgs  = s.aiMessages[threadKey] ?? [];
              const fixedMsgs = prevMsgs.map(m => ({ ...m, thread_id: tid }));

              const nextMessages: Record<string, AIMessage[]> = { ...s.aiMessages, [tid]: fixedMsgs };
              if (threadKey !== tid) delete nextMessages[threadKey];

              return {
                aiActiveThreadId: tid,
                aiIsLoading:      false,
                aiIsMemorySaving: false,
                aiMemorySaved:    memorySaved,
                aiThreads:        newThreads,
                aiMessages:       nextMessages,
              };
            });
          },

          onError: (msg) => {
            set(s => ({
              aiError:      msg,
              aiIsLoading:  false,
              aiIsMemorySaving: false,
              aiMessages: {
                ...s.aiMessages,
                [threadKey]: (s.aiMessages[threadKey] ?? [])
                  .filter(m => m.id !== assistantMsgId),
              },
            }));
          },
        },
      );
    } catch (err) {
      set(s => ({
        aiError:     (err as Error).message,
        aiIsLoading: false,
        aiIsMemorySaving: false,
        aiMessages:  {
          ...s.aiMessages,
          [threadKey]: (s.aiMessages[threadKey] ?? [])
            .filter(m => m.id !== assistantMsgId),
        },
      }));
    }
  },

  aiSetExpertMode:    (v) => set({ aiIsExpertMode: v }),
  aiSetIncludeSensor: (v) => set({ aiIncludeSensor: v }),

  aiLoadKnowledge: async () => {
    const entries = await aiChatService.fetchKnowledgeEntries();
    set({ aiKnowledge: entries });
  },
});
